import { canvasToPDFFile, PDF_QUALITY } from '@utils/pdf-utils';

// Maximum file size for scanned documents (10MB)
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const canvasToPDF = async (
  canvas: HTMLCanvasElement,
  filename: string = 'scanned-document.pdf',
  quality: number = PDF_QUALITY
): Promise<File> => {
  try {
    return canvasToPDFFile(canvas, filename, quality);
  } catch (error) {
    console.error('Error converting canvas to PDF:', error);
    throw new Error('Failed to convert scanned document to PDF');
  }
};

export const isCameraSupported = (): boolean => {
  return !!navigator.mediaDevices?.getUserMedia;
};

export const getCameraStream = async (
  preferredCamera: 'user' | 'environment' = 'environment'
): Promise<MediaStream> => {
  try {
    if (!isCameraSupported()) {
      throw new Error('Camera is not supported in this browser');
    }

    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: preferredCamera,
        width: { ideal: 2048 },
        height: { ideal: 1536 },
      },
      audio: false,
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return stream;
  } catch (error) {
    console.error('Error accessing camera:', error);

    if (error instanceof Error) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        throw new Error(
          'Camera permission denied. Please allow camera access to scan documents.'
        );
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        throw new Error(
          'No camera found. Please ensure your device has a camera.'
        );
      } else if (
        error.name === 'NotReadableError' ||
        error.name === 'TrackStartError'
      ) {
        throw new Error('Camera is already in use by another application.');
      }
    }

    throw new Error('Failed to access camera. Please check your permissions.');
  }
};

export const stopCameraStream = (stream: MediaStream | null): void => {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
};

export const generateScanFilename = (): string => {
  const timestamp = new Date().toISOString().split('T')[0];
  const randomId = Math.random().toString(36).substring(2, 8);
  return `scanned-document-${timestamp}-${randomId}.pdf`;
};

export const estimateCanvasPDFSize = (
  canvas: HTMLCanvasElement,
  quality: number = PDF_QUALITY
): number => {
  const dataURL = canvas.toDataURL('image/jpeg', quality);
  const base64Data = dataURL.split(',')[1];
  const estimatedImageSize = (base64Data.length * 3) / 4;
  return Math.ceil(estimatedImageSize * 1.15);
};

export const canvasToPDFWithSizeLimit = async (
  canvas: HTMLCanvasElement,
  filename: string = 'scanned-document.pdf',
  maxSize: number = MAX_FILE_SIZE
): Promise<File> => {
  let quality = PDF_QUALITY;
  let attempts = 0;
  const maxAttempts = 5;

  while (attempts < maxAttempts) {
    const estimatedSize = estimateCanvasPDFSize(canvas, quality);

    if (estimatedSize <= maxSize) {
      const file = await canvasToPDF(canvas, filename, quality);

      if (file.size <= maxSize) {
        return file;
      }

      quality *= 0.8;
      attempts++;
      continue;
    }

    quality *= 0.8;
    attempts++;

    // Don't go below minimum quality
    if (quality < 0.3) {
      throw new Error(
        `Unable to compress document to under ${(maxSize / 1024 / 1024).toFixed(1)}MB. ` +
          'Please try scanning a smaller area or use lower resolution.'
      );
    }
  }

  throw new Error(
    'Failed to create PDF within size limits after multiple attempts'
  );
};
